import { queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { api, unwrap } from '~/lib/api'

export type Competition = 'competitive' | 'league' | 'cup' | 'friendly' | 'all'
export const COMPETITION_LABELS: Record<Competition, string> = {
  competitive: 'Pflichtspiele',
  league: 'Liga',
  cup: 'Pokal',
  friendly: 'Testspiele',
  all: 'Alle',
}
/** One option list for every competition control (Statistik, Vergleich, Leistung). */
export const COMPETITION_OPTIONS = (Object.keys(COMPETITION_LABELS) as Competition[]).map((c) => ({ value: c, label: COMPETITION_LABELS[c] }))

export type Venue = 'home' | 'away'
export type Surface = 'natural_grass' | 'artificial_turf' | 'hybrid' | 'indoor' | 'ash'
export const SURFACE_LABELS: Record<Surface, string> = {
  natural_grass: 'Rasen',
  artificial_turf: 'Kunstrasen',
  hybrid: 'Hybridrasen',
  indoor: 'Halle',
  ash: 'Tenne',
}

/** Shared statistics filters (spec kader §7.9); missing = no restriction. */
export type StatsFilter = { competition: Competition; venue?: Venue; surface?: Surface }

// German search values for shared links (?ort=heim&untergrund=kunstrasen).
export const VENUE_PARAM: Record<Venue, string> = { home: 'heim', away: 'auswaerts' }
export const SURFACE_PARAM: Record<Surface, string> = {
  natural_grass: 'rasen',
  artificial_turf: 'kunstrasen',
  hybrid: 'hybrid',
  indoor: 'halle',
  ash: 'tenne',
}
const keyOf = <K extends string>(map: Record<K, string>, v: unknown) =>
  (Object.entries(map) as [K, string][]).find(([, p]) => p === v)?.[0]
export const venueOfParam = (v: unknown) => keyOf(VENUE_PARAM, v)
export const surfaceOfParam = (v: unknown) => keyOf(SURFACE_PARAM, v)

const filterQuery = (f: StatsFilter) => ({
  competition: f.competition,
  ...(f.venue ? { venue: f.venue } : {}),
  ...(f.surface ? { surface: f.surface } : {}),
})

// Keys start with 'stats' (server invalidates ['stats'] after imports and squad edits).
export const statsKeys = {
  team: (teamId: string, season: string, f: StatsFilter) => ['stats', 'team', teamId, season, f.competition, f.venue ?? '', f.surface ?? ''] as const,
  players: (teamId: string, season: string, f: StatsFilter) => ['stats', 'players', teamId, season, f.competition, f.venue ?? '', f.surface ?? ''] as const,
  compare: (teamId: string, season: string, c: Competition, ids: string[]) => ['stats', 'compare', teamId, season, c, ids.join(',')] as const,
  person: (id: string, teamId: string, season: string, c: Competition) => ['stats', 'person', id, teamId, season, c] as const,
}

export type TeamStats = InferResponseType<(typeof api.teams)[':teamId']['stats']['$get'], 200>
export type PlayerStats = InferResponseType<(typeof api.teams)[':teamId']['stats']['players']['$get'], 200>
export type PlayerStatRow = PlayerStats['rows'][number]
export type Compare = InferResponseType<(typeof api.stats)['compare']['$get'], 200>
export type PersonStats = InferResponseType<(typeof api.people)[':id']['stats']['$get'], 200>

export const teamStatsQuery = (teamId: string, season: string, filter: StatsFilter) =>
  queryOptions({
    queryKey: statsKeys.team(teamId, season, filter),
    queryFn: (): Promise<TeamStats> =>
      unwrap(api.teams[':teamId'].stats.$get({ param: { teamId }, query: { season, ...filterQuery(filter) } })),
  })

export const playerStatsQuery = (teamId: string, season: string, filter: StatsFilter) =>
  queryOptions({
    queryKey: statsKeys.players(teamId, season, filter),
    queryFn: (): Promise<PlayerStats> =>
      unwrap(api.teams[':teamId'].stats.players.$get({ param: { teamId }, query: { season, ...filterQuery(filter) } })),
  })

export const compareQuery = (teamId: string, season: string, competition: Competition, personIds: string[]) =>
  queryOptions({
    queryKey: statsKeys.compare(teamId, season, competition, personIds),
    queryFn: (): Promise<Compare> =>
      unwrap(api.stats.compare.$get({ query: { teamId, season, competition, personIds: personIds.join(',') } })),
    placeholderData: (prev) => prev,
  })

export const personStatsQuery = (id: string, teamId: string, season: string, competition: Competition = 'competitive') =>
  queryOptions({
    queryKey: statsKeys.person(id, teamId, season, competition),
    queryFn: (): Promise<PersonStats> => unwrap(api.people[':id'].stats.$get({ param: { id }, query: { teamId, season, competition } })),
  })

export const csvHref = (teamId: string, season: string, filter: StatsFilter) =>
  `/api/teams/${teamId}/stats/players.csv?${new URLSearchParams({ season, ...filterQuery(filter) })}`

export const isCompetition = (v: unknown): v is Competition =>
  typeof v === 'string' && v in COMPETITION_LABELS
